import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { GlbPreview } from "../three/GlbPreview";
import { detectBrowserCapabilities, preferredRecorderMimeType } from "../utils/capabilities";
import { exportOneSecondClip, type CropRect, type TrimResult } from "../utils/videoTrim";
import { loadGlbObjectUrl } from "../utils/glbFile";

type VideoMeta = {
  duration: number;
  width: number;
  height: number;
};

type ProbeStatus = "idle" | "running" | "success" | "error";

type FileChooserProbe = {
  accept?: string;
  capture?: "environment";
  description: string;
  id: string;
  title: string;
};

const defaultCrop: CropRect = {
  x: 0.18,
  y: 0.12,
  width: 0.64,
  height: 0.76
};

const fileChooserProbes: FileChooserProbe[] = [
  {
    accept: "video/*",
    description: "当前主流程使用方式。Android 学习通如果直进拍摄，问题大概率在这里。",
    id: "video-wildcard",
    title: "video/*"
  },
  {
    accept: "video/mp4,video/quicktime,video/x-m4v,.mp4,.mov,.m4v",
    description: "更明确地列出常见视频格式，测试是否能调起相册或文件选择。",
    id: "explicit-video",
    title: "明确视频格式"
  },
  {
    description: "不传 accept，让系统打开通用文件入口，再由网页判断是否为视频。",
    id: "any-file",
    title: "不限制文件类型"
  },
  {
    accept: "video/*",
    capture: "environment",
    description: "显式拍摄入口。生产版可以把它和选择已有视频分开。",
    id: "capture-video",
    title: "拍摄视频"
  }
];

export function CompatPage() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [capabilities, setCapabilities] = useState(() => detectBrowserCapabilities());
  const [chooserResults, setChooserResults] = useState<Record<string, string>>({});
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoMeta, setVideoMeta] = useState<VideoMeta | null>(null);
  const [trimStart, setTrimStart] = useState(0);
  const [cropRect, setCropRect] = useState<CropRect>(defaultCrop);
  const [trimStatus, setTrimStatus] = useState<ProbeStatus>("idle");
  const [trimMessage, setTrimMessage] = useState("等待选择视频");
  const [trimResult, setTrimResult] = useState<TrimResult | null>(null);
  const [modelStatus, setModelStatus] = useState<ProbeStatus>("idle");
  const [modelMessage, setModelMessage] = useState("选择 .glb 或包含 .glb 的 .zip 文件");
  const [glbUrl, setGlbUrl] = useState<string | null>(null);

  const selectedMime = useMemo(() => preferredRecorderMimeType(), []);
  const maxTrimStart = Math.max(0, Math.floor((videoMeta?.duration ?? 1) - 1));

  useEffect(() => {
    return () => {
      if (videoUrl) URL.revokeObjectURL(videoUrl);
      if (trimResult?.url) URL.revokeObjectURL(trimResult.url);
      if (glbUrl) URL.revokeObjectURL(glbUrl);
    };
  }, [glbUrl, trimResult, videoUrl]);

  function refreshCapabilities() {
    setCapabilities(detectBrowserCapabilities());
  }

  function applySelectedVideo(file: File | null, emptyMessage: string, selectedMessage?: string) {
    setVideoFile(file);
    setVideoMeta(null);
    setTrimResult(null);
    setTrimStatus("idle");

    if (videoUrl) URL.revokeObjectURL(videoUrl);
    if (!file) {
      setVideoUrl(null);
      setTrimMessage(emptyMessage);
      return;
    }

    const nextUrl = URL.createObjectURL(file);
    setVideoUrl(nextUrl);
    setTrimMessage(selectedMessage ?? `已选择 ${file.name}`);
  }

  function handleVideoSelected(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    applySelectedVideo(file, "未选择视频");
  }

  function handleChooserProbeSelected(probe: FileChooserProbe, event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    if (!file) {
      setChooserResults((value) => ({ ...value, [probe.id]: "未选择文件" }));
      return;
    }

    const fileSummary = `${file.name} · ${file.type || "未知格式"} · ${formatBytes(file.size)}`;
    const isVideo = file.type.startsWith("video/") || /\.(mp4|mov|m4v|webm|avi|mkv)$/i.test(file.name);
    setChooserResults((value) => ({
      ...value,
      [probe.id]: isVideo ? fileSummary : `${fileSummary} · 不是视频`
    }));

    if (isVideo) {
      applySelectedVideo(file, "未选择视频", `通过「${probe.title}」选择：${file.name}`);
    }
  }

  function handleVideoLoaded() {
    const video = videoRef.current;
    if (!video) return;

    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    setVideoMeta({
      duration,
      width: video.videoWidth,
      height: video.videoHeight
    });
    setTrimStart(0);
  }

  async function handleTrimProbe() {
    const video = videoRef.current;
    if (!video || !videoFile) {
      setTrimStatus("error");
      setTrimMessage("请先选择并加载视频");
      return;
    }

    if (trimResult?.url) {
      URL.revokeObjectURL(trimResult.url);
      setTrimResult(null);
    }

    setTrimStatus("running");
    setTrimMessage("正在导出 1 秒裁剪片段...");

    try {
      const result = await exportOneSecondClip({
        video,
        startTime: trimStart,
        cropRect,
        mimeType: selectedMime
      });
      setTrimResult(result);
      setTrimStatus("success");
      setTrimMessage(`导出成功：${formatBytes(result.blob.size)}，格式 ${result.blob.type || "未知格式"}`);
    } catch (error) {
      setTrimStatus("error");
      setTrimMessage(error instanceof Error ? error.message : "导出失败");
    }
  }

  async function handleModelSelected(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    if (glbUrl) {
      URL.revokeObjectURL(glbUrl);
      setGlbUrl(null);
    }

    if (!file) {
      setModelStatus("idle");
      setModelMessage("未选择模型文件");
      return;
    }

    setModelStatus("running");
    setModelMessage(`正在读取 ${file.name}...`);

    try {
      const objectUrl = await loadGlbObjectUrl(file);
      setGlbUrl(objectUrl);
      setModelStatus("success");
      setModelMessage("模型文件已读取，正在渲染");
    } catch (error) {
      setModelStatus("error");
      setModelMessage(error instanceof Error ? error.message : "模型读取失败");
    }
  }

  return (
    <div className="compat-page">
      <section className="hero-panel">
        <div>
          <p className="eyebrow">WebView Capability Probe</p>
          <h2>学习通环境先跑这一页</h2>
          <p className="lede">
            这一页只验证 H5 能力，不接业务数据。重点检查视频选择、1 秒前端裁剪、WebGL 和 GLB/ZIP 渲染。
          </p>
        </div>
        <button className="ghost-button" type="button" onClick={refreshCapabilities}>
          重新检测
        </button>
      </section>

      <section className="grid two-col">
        <CapabilityCard
          title="浏览器能力"
          items={[
            ["User Agent", capabilities.userAgent],
            ["安全上下文", capabilities.secureContext ? "是" : "否"],
            ["WebGL", capabilities.webgl ? "可用" : "不可用"],
            ["WebGL2", capabilities.webgl2 ? "可用" : "不可用"],
            ["MediaRecorder", capabilities.mediaRecorder ? "可用" : "不可用"],
            ["Canvas captureStream", capabilities.canvasCaptureStream ? "可用" : "不可用"],
            ["WebCodecs", capabilities.webCodecs ? "可用" : "不可用"],
            ["推荐录制格式", selectedMime || "未找到可用格式"]
          ]}
        />

        <div className="card">
          <div className="card-header">
            <div>
              <p className="eyebrow">Recorder MIME</p>
              <h3>可用导出格式</h3>
            </div>
            <StatusPill status={capabilities.supportedRecorderTypes.length ? "success" : "error"} />
          </div>
          <div className="mime-list">
            {capabilities.recorderCandidates.map((type) => (
              <div className="mime-row" key={type}>
                <span>{type}</span>
                <strong>{capabilities.supportedRecorderTypes.includes(type) ? "支持" : "不支持"}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="card">
        <div className="card-header">
          <div>
            <p className="eyebrow">Android File Picker Probe</p>
            <h3>Android 文件选择入口</h3>
          </div>
        </div>
        <p className="status-text">
          在 Android 学习通里逐个点击下面四个入口，记录哪个能打开相册或文件里的已有视频。选中视频后会自动载入下方裁剪测试区。
        </p>
        <div className="chooser-grid">
          {fileChooserProbes.map((probe) => (
            <label className="chooser-card" key={probe.id}>
              <input
                accept={probe.accept}
                capture={probe.capture}
                type="file"
                onChange={(event) => handleChooserProbeSelected(probe, event)}
                onClick={(event) => {
                  event.currentTarget.value = "";
                }}
              />
              <span>{probe.title}</span>
              <small>{probe.description}</small>
              <strong>{chooserResults[probe.id] ?? "未测试"}</strong>
            </label>
          ))}
        </div>
      </section>

      <section className="grid two-col main-tools">
        <div className="card">
          <div className="card-header">
            <div>
              <p className="eyebrow">Video Trim Probe</p>
              <h3>1 秒裁剪导出</h3>
            </div>
            <StatusPill status={trimStatus} />
          </div>

          <label className="file-drop">
            <input accept="video/*" type="file" onChange={handleVideoSelected} />
            <span>选择本地视频</span>
            <small>{videoFile ? videoFile.name : "支持系统视频选择器，学习通里重点测试这里"}</small>
          </label>

          <div className="video-stage">
            {videoUrl ? (
              <>
                <video
                  ref={videoRef}
                  controls
                  muted
                  playsInline
                  preload="metadata"
                  src={videoUrl}
                  onLoadedMetadata={handleVideoLoaded}
                />
                <div
                  className="crop-box"
                  style={{
                    left: `${cropRect.x * 100}%`,
                    top: `${cropRect.y * 100}%`,
                    width: `${cropRect.width * 100}%`,
                    height: `${cropRect.height * 100}%`
                  }}
                />
              </>
            ) : (
              <div className="empty-state">等待选择视频</div>
            )}
          </div>

          {videoMeta && (
            <div className="meta-strip">
              <span>{videoMeta.width} x {videoMeta.height}</span>
              <span>{videoMeta.duration.toFixed(2)}s</span>
              <span>{videoFile ? formatBytes(videoFile.size) : ""}</span>
            </div>
          )}

          <div className="control-stack">
            <RangeControl
              label="开始时间"
              max={maxTrimStart}
              min={0}
              step={0.1}
              suffix="s"
              value={trimStart}
              onChange={setTrimStart}
            />
            <RangeControl label="裁剪 X" max={0.95} min={0} step={0.01} value={cropRect.x} onChange={(x) => setCropRect((value) => clampCrop({ ...value, x }))} />
            <RangeControl label="裁剪 Y" max={0.95} min={0} step={0.01} value={cropRect.y} onChange={(y) => setCropRect((value) => clampCrop({ ...value, y }))} />
            <RangeControl label="裁剪宽" max={1} min={0.05} step={0.01} value={cropRect.width} onChange={(width) => setCropRect((value) => clampCrop({ ...value, width }))} />
            <RangeControl label="裁剪高" max={1} min={0.05} step={0.01} value={cropRect.height} onChange={(height) => setCropRect((value) => clampCrop({ ...value, height }))} />
          </div>

          <button className="primary-button" disabled={!videoUrl || trimStatus === "running"} type="button" onClick={handleTrimProbe}>
            导出 1 秒裁剪片段
          </button>
          <p className="status-text">{trimMessage}</p>

          {trimResult && (
            <div className="trim-result">
              <video controls playsInline src={trimResult.url} />
              <a className="download-link" download={`pingpi-trim.${extensionForMime(trimResult.blob.type)}`} href={trimResult.url}>
                下载导出片段
              </a>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <div>
              <p className="eyebrow">GLB Render Probe</p>
              <h3>GLB / ZIP 渲染</h3>
            </div>
            <StatusPill status={modelStatus} />
          </div>

          <label className="file-drop">
            <input accept=".glb,.zip,model/gltf-binary,application/zip" type="file" onChange={handleModelSelected} />
            <span>选择 GLB 或 ZIP</span>
            <small>ZIP 中需要包含且只需包含一个 .glb 文件</small>
          </label>

          <GlbPreview glbUrl={glbUrl} onError={(message) => {
            setModelStatus("error");
            setModelMessage(message);
          }} onReady={() => {
            setModelStatus("success");
            setModelMessage("模型渲染成功，可拖动、缩放、旋转");
          }} />

          <p className="status-text">{modelMessage}</p>
        </div>
      </section>
    </div>
  );
}

function CapabilityCard({ title, items }: { title: string; items: Array<[string, string]> }) {
  return (
    <div className="card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Runtime</p>
          <h3>{title}</h3>
        </div>
      </div>
      <dl className="capability-list">
        {items.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function StatusPill({ status }: { status: ProbeStatus }) {
  const labels: Record<ProbeStatus, string> = {
    idle: "待测",
    running: "检测中",
    success: "通过",
    error: "失败"
  };
  return <span className={`status-pill ${status}`}>{labels[status]}</span>;
}

function RangeControl({
  label,
  max,
  min,
  onChange,
  step,
  suffix = "",
  value
}: {
  label: string;
  max: number;
  min: number;
  onChange: (value: number) => void;
  step: number;
  suffix?: string;
  value: number;
}) {
  return (
    <label className="range-control">
      <span>{label}</span>
      <input
        max={max}
        min={min}
        step={step}
        type="range"
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <strong>{value.toFixed(step < 0.1 ? 2 : 1)}{suffix}</strong>
    </label>
  );
}

function clampCrop(rect: CropRect): CropRect {
  const width = Math.min(1, Math.max(0.05, rect.width));
  const height = Math.min(1, Math.max(0.05, rect.height));
  return {
    width,
    height,
    x: Math.min(1 - width, Math.max(0, rect.x)),
    y: Math.min(1 - height, Math.max(0, rect.y))
  };
}

function formatBytes(value: number) {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

function extensionForMime(mime: string) {
  if (mime.includes("mp4")) return "mp4";
  if (mime.includes("webm")) return "webm";
  return "webm";
}
